import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["appearance", "avatar", "children", "chipElevation", "closeButtonVoiceOver", "defaultOpen", "disabled", "icon", "isOpen", "onClose", "size", "renderCloseButton", "textTransform"];
import PropTypes from 'prop-types';
import React, { cloneElement, forwardRef, useCallback, useState } from 'react';
import { ChipLabel } from '../AssistChip/ChipLabel';
import { TagCloseButton } from '../TagCloseButton/TagCloseButton';
import { StyledInputChip } from './InputChip.styles';

/**
 * Input chips are used to hold text that has been entered or selected
 * by the user (ex: multiple Combobox selections, addresses in an email's "to:" field).
 * When text is converted into an input chip, it helps confirm a user's input.
 *
 * @related ['AssistChip', 'Combobox', 'FilterChip', 'Tag']
 * @since 8.97.0
 */
export var InputChip = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var appearance = props.appearance,
    avatar = props.avatar,
    children = props.children,
    chipElevation = props.chipElevation,
    closeButtonVoiceOver = props.closeButtonVoiceOver,
    defaultOpen = props.defaultOpen,
    disabled = props.disabled,
    icon = props.icon,
    controlledOpen = props.isOpen,
    onClose = props.onClose,
    size = props.size,
    renderCloseButton = props.renderCloseButton,
    textTransform = props.textTransform,
    rest = _objectWithoutProperties(props, _excluded);
  var _useState = useState(defaultOpen),
    _useState2 = _slicedToArray(_useState, 2),
    uncontrolledOpen = _useState2[0],
    setUncontrolledOpen = _useState2[1];
  var isOpen = uncontrolledOpen;
  if (typeof controlledOpen !== 'undefined') {
    isOpen = controlledOpen;
  }
  var closeCallback = useCallback(function () {
    setUncontrolledOpen(false);
    if (typeof onClose === 'function') {
      onClose(props);
    }
  }, [onClose, props, setUncontrolledOpen]);
  if (typeof isOpen === 'boolean' && !isOpen) {
    return null;
  }
  var adjustedAvatar;
  if (avatar) {
    // Prevent img alt text from being read and
    // chip from being announced as a "button, group"
    adjustedAvatar = /*#__PURE__*/cloneElement(avatar, {
      'aria-hidden': 'true',
      'as': 'span'
    });
  }
  var closeButton;
  if (isOpen && typeof renderCloseButton === 'function') {
    closeButton = renderCloseButton({
      'aria-label': closeButtonVoiceOver || "Remove " + children,
      'aria-disabled': disabled,
      disabled: disabled,
      'onClick': closeCallback
    });
  }
  var chip = /*#__PURE__*/React.createElement(StyledInputChip, _extends({
    appearance: "gray",
    chipElevation: chipElevation,
    disabled: disabled,
    ref: forwardedRef,
    size: "md",
    textTransform: "none"
  }, rest), avatar ? adjustedAvatar : null, icon, /*#__PURE__*/React.createElement(ChipLabel, null, children), closeButton);
  return chip;
});
InputChip.displayName = 'InputChip';
InputChip.propTypes = {
  /**
   * Adds a leading avatar. Takes an `Avatar` component. Ignores `Avatar`’s `size` prop
   * (i.e. the avatar size is fixed).
   */
  avatar: PropTypes.node,
  /**
   * The input chip’s text label. To add decorators, use the `icon` and `avatar` props.
   */
  children: PropTypes.node.isRequired,
  /**
   * When true, adds a drop shadow. Useful for making the input chip easier to see on
   * certain backgrounds (ex: on top of a photo or map)
   */
  chipElevation: PropTypes.bool,
  /**
   * When enabled, the input chip will close in an uncontrolled manner.
   */
  defaultOpen: PropTypes.bool,
  /**
   * Sets the input chip as disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Adds a leading icon. Takes an `Icon` component but ignores its `size` prop
   * (i.e. the icon size is fixed). Does not support `DetailedIcon`.
   */
  icon: PropTypes.node,
  /**
   * When enabled, the input chip will close in a controlled manner.
   */
  isOpen: PropTypes.bool,
  /**
   * The text that will be announced to screen readers when you focus on the
   * chip's close button. Used as the close button's `aria-label` value.
   *
   * Defaults to "Remove {chip label}"
   */
  closeButtonVoiceOver: PropTypes.string,
  /**
   * Function called when the chip is closed. The chip’s props will be passed as
   * the only argument.
   */
  onClose: PropTypes.func,
  /**
   * A render function that returns a close button.
   */
  renderCloseButton: PropTypes.func
};
InputChip.defaultProps = {
  avatar: null,
  chipElevation: false,
  disabled: false,
  icon: null,
  renderCloseButton: function renderCloseButton(props) {
    return /*#__PURE__*/React.createElement(TagCloseButton, props);
  }
};